import {Capacitor} from "@capacitor/core";
import {useMainStore, useGameStore} from "@/store";
import request from "@/services/request";
import EventBus from "@/services/eventBus";

let admobPlugin = null;
let initialized = false;
let adLoadedResolve = null;
let ssvTimeoutTimer = null;

const SSV_TIMEOUT_MS = 30000;

/**
 * Initialize AdMob on native platforms when ads are enabled.
 */
async function initialize() {
    const store = useMainStore();

    if (!Capacitor.isNativePlatform() || !store.ads_enabled) {
        return;
    }

    if (initialized) {
        return;
    }

    const {AdMob, MaxAdContentRating} = await import('@capacitor-community/admob');
    admobPlugin = AdMob;

    const initOptions = {
        testingDevices: [],
        initializeForTesting: false,
    };

    if (store.coppa) {
        initOptions.tagForChildDirectedTreatment = true;
        initOptions.maxAdContentRating = MaxAdContentRating.General;
    }

    await admobPlugin.initialize(initOptions);
    initialized = true;

    // Listen for ad events
    admobPlugin.addListener('onRewardedVideoAdLoaded', () => {
        useGameStore().SET_AD_READY(true);
        useGameStore().SET_AD_LOADING(false);
        if (adLoadedResolve) {
            adLoadedResolve();
            adLoadedResolve = null;
        }
    });

    admobPlugin.addListener('onRewardedVideoAdFailedToLoad', () => {
        useGameStore().SET_AD_READY(false);
        useGameStore().SET_AD_LOADING(false);
        if (adLoadedResolve) {
            adLoadedResolve = null;
        }
    });

    preload();
}

/**
 * Preload a rewarded video ad so it's ready when needed.
 */
async function preload() {
    if (!admobPlugin || !initialized) {
        return;
    }

    const store = useMainStore();
    const gameStore = useGameStore();

    if (!store.ads_enabled || !store.ads_rewarded_unit_id) {
        return;
    }

    gameStore.SET_AD_LOADING(true);

    try {
        await admobPlugin.prepareRewardVideoAd({
            adId: store.ads_rewarded_unit_id,
        });
    } catch {
        gameStore.SET_AD_LOADING(false);
        gameStore.SET_AD_READY(false);
    }
}

/**
 * Load and show a rewarded video ad.
 *
 * Covers everything up to and including the ad playing: requesting the ULID,
 * preparing the ad with SSV options, and showing it. Resolves once the user
 * returns from the ad, at which point the reward still needs to be verified
 * server-side via verifyReward().
 *
 * Returns the ULID that verifyReward() must be called with.
 */
async function loadAndShowAd() {
    const store = useMainStore();
    const gameStore = useGameStore();

    // Request ULID and callback URL from the API
    const {data} = await request('ads/initiate', {
        method: 'post',
        data: {
            placement: 'post_loss_buff',
            platform: Capacitor.getPlatform(),
        },
    });

    const ulid = data.ulid;

    gameStore.SET_PENDING_AD_ULID(ulid);

    // Set SSV options on the ad before showing
    await admobPlugin.prepareRewardVideoAd({
        adId: store.ads_rewarded_unit_id,
        ssv: {
            customData: ulid,
            userId: store.id,
        },
    });

    // Show the ad
    await admobPlugin.showRewardVideoAd();

    gameStore.SET_AD_READY(false);

    return ulid;
}

/**
 * Verify the reward for a shown ad via SSV.
 *
 * Called after the user returns from the ad (loadAndShowAd resolved). Waits for
 * server-side verification to arrive over the websocket and resolves with the
 * reward, or rejects with an 'SSV verification timeout' error.
 */
function verifyReward(ulid) {
    const gameStore = useGameStore();

    return new Promise((resolve, reject) => {
        const onVerified = (message) => {
            if (message.ulid === ulid) {
                clearTimeout(ssvTimeoutTimer);
                ssvTimeoutTimer = null;
                EventBus.off('ad-reward-verified', onVerified);
                gameStore.SET_PENDING_AD_ULID(null);
                resolve(message.reward);
                // Preload next ad
                preload();
            }
        };

        EventBus.on('ad-reward-verified', onVerified);

        ssvTimeoutTimer = setTimeout(() => {
            EventBus.off('ad-reward-verified', onVerified);
            gameStore.SET_PENDING_AD_ULID(null);
            reject(new Error('SSV verification timeout'));
            // Preload next ad for retry
            preload();
        }, SSV_TIMEOUT_MS);
    });
}

/**
 * Show a rewarded video ad with SSV verification.
 * Returns a promise that resolves with the reward or rejects on failure.
 */
async function showRewardedAd() {
    const ulid = await loadAndShowAd();
    return verifyReward(ulid);
}

/**
 * Check if ads are available (native + enabled + loaded).
 */
function isAvailable() {
    const store = useMainStore();
    const gameStore = useGameStore();

    if (!Capacitor.isNativePlatform() || !store.ads_enabled) {
        return false;
    }

    return gameStore.adReady;
}

/**
 * Check if we should use the placeholder flow instead of real ads.
 */
function usePlaceholder() {
    const store = useMainStore();
    return !Capacitor.isNativePlatform() || !store.ads_enabled;
}

export default {
    initialize,
    preload,
    loadAndShowAd,
    verifyReward,
    showRewardedAd,
    isAvailable,
    usePlaceholder,
};
